import { useMutation, useQuery } from "@tanstack/solid-query";
import {
  Accessor,
  createContext,
  createMemo,
  createSignal,
  ParentComponent,
  useContext,
} from "solid-js";

import gqlClient from "../../schema/gqlAuthorizedRequest.js";
import {
  Notification,
  UserNotificationPreferences,
} from "../../schema/graphql.js";
import { graphql } from "../../schema/index.js";
import { useAuth } from "../auth/AuthContext.js";

export const NotificationContext = createContext<{
  notifications: Accessor<Notification[]>;
  unreadNotificationIds: Accessor<number[]>;
  clearAllNotifications: () => void;
  markNotificationAsRead: (notificationId: number) => void;
  notificationPreferences: () => UserNotificationPreferences[] | undefined;
}>();

const getWebAppNotificationsQuery = graphql(`
  query getWebAppNotifications($after: Int) {
    getWebAppNotifications(after: $after) {
      id
      onboardingUserId
      notificationTitle
      notificationBody
      createdAt
      readAt
    }
  }
`);

const getUserNotificationPreferencesQuery = graphql(`
  query notificationPreferences {
    getNotificationPreferences {
      type
      preference {
        emailEnabled
        webAppEnabled
      }
    }
  }
`);

const markNotificationAsReadMutationGql = graphql(`
  mutation markNotificationAsRead($notificationId: Int!) {
    markNotificationAsRead(notificationId: $notificationId) {
      success
    }
  }
`);

const clearAllNotificationsMutationGql = graphql(`
  mutation deleteAllNotifications {
    deleteAllNotifications {
      success
    }
  }
`);

export const NotificationContextProvider: ParentComponent = (props) => {
  const [userNotifications, setUserNotifications] = createSignal<
    Notification[]
  >([]);

  const auth = useAuth();

  const unreadNotificationIds = createMemo(() => {
    return userNotifications()
      .filter((n) => n.readAt === null)
      .map((n) => n.id);
  });

  useQuery(() => ({
    queryKey: ["notifications"],
    queryFn: async () => {
      const afterValue = userNotifications().length
        ? (userNotifications().at(0)?.id ?? null)
        : null;
      const data = await gqlClient.request(getWebAppNotificationsQuery, {
        after: afterValue,
      });
      if (userNotifications().length === 0) {
        setUserNotifications(data.getWebAppNotifications);
      } else {
        setUserNotifications([
          ...data.getWebAppNotifications,
          ...userNotifications(),
        ]);
      }
      return data;
    },
    refetchInterval: 60 * 1000,
    get enabled() {
      return !!auth?.authenticatedUser();
    },
  }));

  const markNotificationAsRead = useMutation(() => ({
    mutationFn: async (variables: { notificationId: number }) => {
      return gqlClient.request(markNotificationAsReadMutationGql, variables);
    },
  }));

  const clearAllNotificationsMutation = useMutation(() => ({
    mutationFn: async () => {
      await gqlClient.request(clearAllNotificationsMutationGql);
      setUserNotifications([]);
    },
  }));

  const notificationPreferences = useQuery(() => ({
    queryKey: ["userNotificationPreferences"],
    queryFn: async () => {
      return await gqlClient.request(getUserNotificationPreferencesQuery);
    },
    get enabled() {
      return !!auth?.authenticatedUser();
    },
  }));

  const markAsRead = (notificationId: number) => {
    const notification = userNotifications().find(
      (n) => n.id === notificationId,
    );
    if (notification?.readAt) {
      return;
    }

    const newNotifications = userNotifications().map((n) => {
      if (n.id === notificationId) {
        return { ...n, readAt: new Date() };
      }
      return n;
    });

    setUserNotifications(newNotifications);
    markNotificationAsRead.mutate({ notificationId });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications: userNotifications,
        unreadNotificationIds,
        markNotificationAsRead: markAsRead,
        clearAllNotifications: () => {
          clearAllNotificationsMutation.mutate();
        },
        notificationPreferences: () =>
          notificationPreferences.data?.getNotificationPreferences,
      }}
    >
      {props.children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
